/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useContext, useMemo } from "react"
import PropTypes from "prop-types"
import { compose } from "recompose"
import capitalize from "lodash.capitalize"
import { useToasts } from "react-toast-notifications"
import { useTranslation } from "react-i18next"

import {
  Box,
  Input,
  Textarea,
  Button,
  VStack,
  Heading,
  Image,
  Text,
  ChakraProvider,
  Checkbox,
  CheckboxGroup,
  Stack,
  Radio,
  RadioGroup,
  Wrap,
  WrapItem,
  Tag,
} from "@chakra-ui/react"

import Nav from "../../components/atoms/Nav"
import { withFirebase } from "../../utils/Firebase"
import { withAuthorization, AuthUserContext } from "../../context/Session"
import * as ROLES from "../../utils/Constants/Roles"
import { postCreatePushNotification } from "../../utils/App"
import countries from "../../utils/countries.json"

const AGE_BANDS = [
  { key: "0_5", label: "0 - 5", min: 0, max: 5 },
  { key: "5_10", label: "5 - 10", min: 5, max: 10 },
  { key: "10_15", label: "10 - 15", min: 10, max: 15 },
  { key: "15_20", label: "15 - 20", min: 15, max: 20 },
  { key: "20_25", label: "20 - 25", min: 20, max: 25 },
  { key: "25_30", label: "25 - 30", min: 25, max: 30 },
  { key: "30_35", label: "30 - 35", min: 30, max: 35 },
  { key: "35_40", label: "35 - 40", min: 35, max: 40 },
  { key: "40_50", label: "40 - 50", min: 40, max: 50 },
  { key: "50_60", label: "50 - 60", min: 50, max: 60 },
  { key: "60_120", label: "60+", min: 60, max: 120 },
]

const slug = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")

const stateId = (state) => `state:${state.id}`
const cityId = (city) => `city:${city.id}`
const districtId = (city, districtName) =>
  `district:${city.id}:${slug(districtName)}`

// Find country by ISO2 (case-insensitive)
const findCountry = (iso2) =>
  countries?.find(
    (c) => c?.iso2?.toLowerCase() === String(iso2 || "").toLowerCase()
  )

// Header checkbox state (Todos)
const allState = (selected, all) => ({
  all: all.length > 0 && selected.length === all.length,
  none: selected.length === 0,
  some: selected.length > 0 && selected.length < all.length,
})

const selectAll = (allItems, setSelected) =>
  setSelected(allItems.map((x) => x.id))
const clearAll = (setSelected) => setSelected([])

const LocalAdminPushNotificationCenter = ({ firebase }) => {
  const authUser = useContext(AuthUserContext)
  const { addToast } = useToasts()
  const { t } = useTranslation()

  // Display collections (each item: { id, name, _raw? })
  const [provinces, setProvinces] = useState([])
  const [cantons, setCantons] = useState([])
  const [districts, setDistricts] = useState([])

  // Selected values (store our generated IDs)
  const [selectedProvinces, setSelectedProvinces] = useState([])
  const [selectedCantons, setSelectedCantons] = useState([])
  const [selectedDistricts, setSelectedDistricts] = useState([])

  const [sex, setSex] = useState("any") // "any" | "male" | "female"
  const [ageBandKeys, setAgeBandKeys] = useState([])

  const [geoLoading, setGeoLoading] = useState(false)
  const [geoError, setGeoError] = useState(null)

  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [file, setFile] = useState(null)
  const [filePreview, setFilePreview] = useState(null)
  const [fileError, setFileError] = useState("")
  const [includeLink, setIncludeLink] = useState(false)
  const [link, setLink] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  // Load provinces from countries.json for the user's business country
  useEffect(() => {
    const loadProvincesFromJson = () => {
      if (!authUser?.countryBusiness) return
      setGeoLoading(true)
      setGeoError(null)
      try {
        const c = findCountry(authUser.countryBusiness)
        const states = c?.states || []
        setProvinces(
          states.map((s) => ({
            id: stateId(s),
            name: (s?.name || "").trim(),
            _raw: s,
          }))
        )
      } catch (err) {
        console.error("Error loading provinces from countries.json", err)
        setGeoError("No se pudieron cargar las provincias.")
      } finally {
        setGeoLoading(false)
      }
    }
    loadProvincesFromJson()
  }, [authUser])

  // Lookup maps for quick joins
  const stateMap = useMemo(() => {
    const m = new Map()
    // eslint-disable-next-line no-underscore-dangle
    for (const p of provinces) m.set(p.id, p._raw)
    return m
  }, [provinces])

  // When provinces change, derive cantons (cities) locally
  useEffect(() => {
    if (!selectedProvinces?.length) {
      setCantons([])
      setSelectedCantons([])
      setDistricts([])
      setSelectedDistricts([])
      return
    }
    setGeoLoading(true)
    try {
      const selectedStates = selectedProvinces
        .map((pid) => stateMap.get(pid))
        .filter(Boolean)
      const mergedCities =
        selectedStates.flatMap((s) =>
          (s?.cities || []).map((c) => ({
            id: cityId(c),
            name: (c?.name || "").trim(),
            _raw: c,
          }))
        ) || []
      setCantons(mergedCities)

      // prune now-invalid selections
      setSelectedCantons((prev) =>
        prev.filter((id) => mergedCities.some((c) => c.id === id))
      )
      setDistricts([])
      setSelectedDistricts([])
    } catch (err) {
      console.error("Error deriving cantons from countries.json", err)
      setGeoError("No se pudieron cargar los cantones.")
    } finally {
      setGeoLoading(false)
    }
  }, [selectedProvinces, stateMap])

  const cityMap = useMemo(() => {
    const m = new Map()
    // eslint-disable-next-line no-underscore-dangle
    for (const c of cantons) m.set(c.id, c._raw)
    return m
  }, [cantons])

  // When cantons change, derive districts locally
  useEffect(() => {
    if (!selectedCantons?.length) {
      setDistricts([])
      setSelectedDistricts([])
      return
    }
    setGeoLoading(true)
    try {
      const selectedCities = selectedCantons
        .map((cid) => cityMap.get(cid))
        .filter(Boolean)
      const mergedDistricts =
        selectedCities.flatMap((city) =>
          (city?.districts || []).map((d) => ({
            id: districtId(city, d?.name || ""),
            name: (d?.name || "").trim(),
            _cityId: cityId(city),
          }))
        ) || []
      setDistricts(mergedDistricts)

      // prune invalid district selections
      setSelectedDistricts((prev) =>
        prev.filter((id) => mergedDistricts.some((d) => d.id === id))
      )
    } catch (err) {
      console.error("Error deriving districts from countries.json", err)
      setGeoError("No se pudieron cargar los distritos.")
    } finally {
      setGeoLoading(false)
    }
  }, [selectedCantons, cityMap])

  const resetForm = () => {
    setTitle("")
    setBody("")
    setFile(null)
    setFilePreview(null)
    setFileError("")
    setIncludeLink(false)
    setLink("")

    setSelectedProvinces([])
    setSelectedCantons([])
    setSelectedDistricts([])
    setSex("any")
    setAgeBandKeys([])

    setIsLoading(false)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    if (fileError) {
      addToast(t("unexpected_error"), {
        appearance: "error",
        placement: "bottom-center",
      })
      setIsLoading(false)
      return
    }

    if (title.trim() === "" || body.trim() === "") {
      addToast("Título y cuerpo son requeridos.", {
        appearance: "error",
        placement: "bottom-center",
      })
      setIsLoading(false)
      return
    }

    try {
      const mapIdsToNames = (ids, collection) =>
        ids
          .map((id) => collection.find((x) => x.id === id)?.name)
          .filter(Boolean)

      const ageRanges = AGE_BANDS.filter((b) =>
        ageBandKeys.includes(b.key)
      ).map(({ min, max }) => ({
        min,
        max,
      }))
      const sexFilter = sex === "any" ? null : sex

      const provinceNames = mapIdsToNames(selectedProvinces, provinces)
      const cantonNames = mapIdsToNames(selectedCantons, cantons)
      const districtNames = mapIdsToNames(selectedDistricts, districts)

      const filters = {
        provinceNames: provinceNames.length ? provinceNames : undefined,
        cantonNames: cantonNames.length ? cantonNames : undefined,
        districtNames: districtNames.length ? districtNames : undefined,
        sex: sexFilter,
        ageRanges: ageRanges.length ? ageRanges : undefined,
        country: authUser?.countryBusiness,
      }

      const users = await firebase.getUsersByFilters(filters)

      const recipients = (users || []).map((a) => a.email).filter(Boolean)

      let imageUrl = null
      if (file) {
        const uploadDir = `organizations/${authUser.organizationId}/notifications/${file.name}`
        const snapshot = await firebase.uploadToStorage(uploadDir, file)
        imageUrl = await snapshot.ref.getDownloadURL()
      }

      const senderUUID =
        crypto.randomUUID() || Math.random().toString(36).slice(2)
      const payload = {
        title,
        body,
        ...(imageUrl && { imageUrl }),
        recipients,
        data: {
          uuid: senderUUID,
          category: "general",
          ...(includeLink && link && { link }),
        },
      }

      const token = await firebase.getIdToken()
      const res = await fetch(postCreatePushNotification, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })

      if (!res.ok) throw new Error("Push notification request failed")

      const notificationRecord = {
        senderUUID,
        title,
        body,
        imageUrl,
        link: includeLink ? link : null,
        recipientCount: recipients.length,
        recipients,
        sentAt: new Date(),
        sentBy: { id: authUser.uid, email: authUser.email },
        filters: {
          provinces: selectedProvinces,
          cantons: selectedCantons,
          districts: selectedDistricts,
          sex: sexFilter,
          ageBands: ageBandKeys,
        },
      }

      await firebase.saveAdminsNotification({
        uid: authUser.uid,
        notification: notificationRecord,
      })

      addToast("Notificación registrada exitosamente.", {
        appearance: "success",
        placement: "bottom-center",
      })

      resetForm()
    } catch (error) {
      console.error("handleSubmit error", error)
      addToast("Error preparando la notificación", {
        appearance: "error",
        placement: "bottom-center",
      })
    }
    setIsLoading(false)
  }

  const handleChange = async (event) => {
    const selectedFile = event.target.files[0]
    if (selectedFile) {
      const validTypes = ["image/jpeg", "image/png"]
      const fileType = selectedFile.type

      if (!validTypes.includes(fileType)) {
        setFile(null)
        setFilePreview(null)
        setFileError("Solo se permiten imágenes en formato JPG o PNG")
        return
      }
      if (selectedFile.size > 300 * 1024) {
        setFile(null)
        setFilePreview(null)
        setFileError("El archivo debe ser menor de 300KB")
        return
      }

      setFile(selectedFile)
      setFilePreview(URL.createObjectURL(selectedFile))
      setFileError("")
    }
  }

  // Helpers to show selected labels
  const nameById = (collection, id) => collection.find((x) => x.id === id)?.name

  return (
    <ChakraProvider>
      <Nav
        centered={false}
        titleKey="messaging_tite"
        descriptionKey="messaging_admin_description"
      />

      <Box p={6} maxW="720px" mx="auto">
        <Heading mb={6}>Enviar Push Notification</Heading>
        <VStack spacing={6} align="stretch">
          {/* ---------- Filters ---------- */}
          <Box>
            <Text fontWeight="semibold" mb={2}>
              Filtros
            </Text>

            {geoLoading && <Text color="gray.500">Cargando filtros...</Text>}
            {geoError && (
              <VStack align="start" spacing={2}>
                <Text color="red.500">{geoError}</Text>
              </VStack>
            )}

            {/* Provinces */}
            <Box mt={3}>
              <Text fontWeight="medium" mb={1}>
                Provincia
              </Text>

              {/* Header “Todos” */}
              {(() => {
                const s = allState(selectedProvinces, provinces)
                return (
                  <Checkbox
                    isChecked={s.all}
                    isIndeterminate={s.some}
                    isDisabled={isLoading}
                    onChange={(e) => {
                      if (e.target.checked) {
                        selectAll(provinces, setSelectedProvinces)
                      } else {
                        clearAll(setSelectedProvinces)
                      }
                    }}
                    mb={2}
                  >
                    Todos
                  </Checkbox>
                )
              })()}

              <CheckboxGroup
                value={selectedProvinces}
                onChange={setSelectedProvinces}
                isDisabled={isLoading}
              >
                <Wrap spacing={2}>
                  {provinces?.map((p) => (
                    <WrapItem key={p.id}>
                      <Checkbox value={p.id}>
                        {capitalize((p.name || "").trim())}
                      </Checkbox>
                    </WrapItem>
                  ))}
                </Wrap>
              </CheckboxGroup>
            </Box>

            {/* Cantons (depends on provinces) */}
            {cantons.length > 0 && (
              <Box mt={4}>
                <Text fontWeight="medium" mb={1}>
                  Cantón
                </Text>

                {/* Header “Todos” */}
                {(() => {
                  const s = allState(selectedCantons, cantons)
                  return (
                    <Checkbox
                      isChecked={s.all}
                      isIndeterminate={s.some}
                      isDisabled={isLoading}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAll(cantons, setSelectedCantons)
                        } else {
                          clearAll(setSelectedCantons)
                        }
                      }}
                      mb={2}
                    >
                      Todos
                    </Checkbox>
                  )
                })()}

                <CheckboxGroup
                  value={selectedCantons}
                  onChange={setSelectedCantons}
                  isDisabled={isLoading}
                >
                  <Wrap spacing={2}>
                    {cantons.map((c) => (
                      <WrapItem key={c.id}>
                        <Checkbox value={c.id}>{capitalize(c.name)}</Checkbox>
                      </WrapItem>
                    ))}
                  </Wrap>
                </CheckboxGroup>
              </Box>
            )}

            {/* Districts (depends on cantons) */}
            {districts.length > 0 && (
              <Box mt={4}>
                <Text fontWeight="medium" mb={1}>
                  Distrito
                </Text>

                {/* Header “Todos” */}
                {(() => {
                  const s = allState(selectedDistricts, districts)
                  return (
                    <Checkbox
                      isChecked={s.all}
                      isIndeterminate={s.some}
                      isDisabled={isLoading}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAll(districts, setSelectedDistricts)
                        } else {
                          clearAll(setSelectedDistricts)
                        }
                      }}
                      mb={2}
                    >
                      Todos
                    </Checkbox>
                  )
                })()}

                <CheckboxGroup
                  value={selectedDistricts}
                  onChange={setSelectedDistricts}
                  isDisabled={isLoading}
                >
                  <Wrap spacing={2}>
                    {districts.map((d) => (
                      <WrapItem key={d.id}>
                        <Checkbox value={d.id}>{capitalize(d.name)}</Checkbox>
                      </WrapItem>
                    ))}
                  </Wrap>
                </CheckboxGroup>
              </Box>
            )}

            {/* Sex */}
            <Box mt={4}>
              <Text fontWeight="medium" mb={1}>
                Sexo
              </Text>
              <RadioGroup value={sex} onChange={setSex} isDisabled={isLoading}>
                <Stack direction="row" spacing={5}>
                  <Radio value="any">Cualquiera</Radio>
                  <Radio value="male">Hombre</Radio>
                  <Radio value="female">Mujer</Radio>
                </Stack>
              </RadioGroup>
            </Box>

            {/* Age Bands */}
            <Box mt={4}>
              <Text fontWeight="medium" mb={1}>
                Rango de edad
              </Text>
              <CheckboxGroup
                value={ageBandKeys}
                onChange={setAgeBandKeys}
                isDisabled={isLoading}
              >
                <Wrap spacing={2}>
                  {AGE_BANDS.map((b) => (
                    <WrapItem key={b.key}>
                      <Checkbox value={b.key}>{b.label}</Checkbox>
                    </WrapItem>
                  ))}
                </Wrap>
              </CheckboxGroup>
            </Box>
          </Box>

          {/* ---------- Message ---------- */}
          <Input
            placeholder="Título (corto)"
            value={title}
            maxLength={60}
            onChange={(e) => setTitle(e.target.value)}
            readOnly={isLoading}
          />

          <Textarea
            placeholder="Cuerpo (max 200 carácteres)"
            value={body}
            maxLength={200}
            onChange={(e) => setBody(e.target.value)}
            readOnly={isLoading}
          />

          <Box>
            <Checkbox
              isDisabled={isLoading}
              isChecked={includeLink}
              onChange={(e) => setIncludeLink(e.target.checked)}
            >
              Incluir un enlace
            </Checkbox>
            {includeLink && (
              <Input
                mt={2}
                placeholder="https://example.com"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
            )}
          </Box>

          <Box>
            <Input
              type="file"
              id="file-upload"
              display="none"
              accept="image/*"
              onChange={handleChange}
            />
            <Button
              as="label"
              htmlFor="file-upload"
              colorScheme="blue"
              isDisabled={isLoading}
            >
              Seleccione un archivo
            </Button>
            {file && !fileError && (
              <Text mt={2}>Archivo seleccionado: {file.name}</Text>
            )}
            {fileError && (
              <Text mt={1} color="red.500">
                {fileError}
              </Text>
            )}
          </Box>

          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isDisabled={
              !!fileError || title.trim() === "" || body.trim() === ""
            }
            isLoading={isLoading}
          >
            Enviar notificación
          </Button>

          {/* ---------- Preview ---------- */}
          <Box
            mt={6}
            p={4}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Heading size="sm" mb={3}>
              Prevista
            </Heading>

            <Box mb={2}>
              <Text fontWeight="semibold">Provincia:</Text>
              <Wrap mt={1}>
                {selectedProvinces.length ? (
                  selectedProvinces.map((id) => (
                    <WrapItem key={id}>
                      <Tag>{capitalize(nameById(provinces, id) || id)}</Tag>
                    </WrapItem>
                  ))
                ) : (
                  <Text color="gray.500">Cualquiera</Text>
                )}
              </Wrap>
            </Box>

            <Box mb={2}>
              <Text fontWeight="semibold">Cantón:</Text>
              <Wrap mt={1}>
                {selectedCantons.length ? (
                  selectedCantons.map((id) => (
                    <WrapItem key={id}>
                      <Tag>{capitalize(nameById(cantons, id) || id)}</Tag>
                    </WrapItem>
                  ))
                ) : (
                  <Text color="gray.500">Cualquiera</Text>
                )}
              </Wrap>
            </Box>

            <Box mb={2}>
              <Text fontWeight="semibold">Distrito:</Text>
              <Wrap mt={1}>
                {selectedDistricts.length ? (
                  selectedDistricts.map((id) => (
                    <WrapItem key={id}>
                      <Tag>{capitalize(nameById(districts, id) || id)}</Tag>
                    </WrapItem>
                  ))
                ) : (
                  <Text color="gray.500">Cualquiera</Text>
                )}
              </Wrap>
            </Box>

            <Text>
              <strong>Sexo:</strong>{" "}
              {sex === "any"
                ? "Cualquiera"
                : sex === "male"
                ? "Hombre"
                : "Mujer"}
            </Text>
            <Box mt={1}>
              <Text fontWeight="semibold">Rango de edad:</Text>
              <Wrap mt={1}>
                {ageBandKeys.length ? (
                  ageBandKeys.map((k) => (
                    <WrapItem key={k}>
                      <Tag>
                        {AGE_BANDS.find((b) => b.key === k)?.label || k}
                      </Tag>
                    </WrapItem>
                  ))
                ) : (
                  <Text color="gray.500">Cualquiera</Text>
                )}
              </Wrap>
            </Box>

            <Text mt={3} fontWeight="bold">
              {title}
            </Text>
            <Text>{body}</Text>
            {includeLink && link && (
              <Text mt={2}>
                <strong>Enlace:</strong>{" "}
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#3182ce" }}
                >
                  {link}
                </a>
              </Text>
            )}
            {filePreview && !fileError && (
              <Image
                src={filePreview}
                alt="Vista previa"
                mt={3}
                maxH="150px"
                objectFit="cover"
                borderRadius="md"
              />
            )}
          </Box>
        </VStack>
      </Box>
    </ChakraProvider>
  )
}

LocalAdminPushNotificationCenter.propTypes = {
  firebase: PropTypes.shape().isRequired,
}

const condition = (authUser) =>
  !!authUser &&
  authUser.userType === "admin" &&
  authUser.roles[ROLES.LOCAL_ADMIN]

const ComposedLocalAdminPushNotificationCenter = compose(
  withFirebase,
  withAuthorization(condition)
)(LocalAdminPushNotificationCenter)

export default ComposedLocalAdminPushNotificationCenter
